import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, ArrowLeft } from 'lucide-react';
import Button from '../components/UI/Button';
import toast from 'react-hot-toast';
import apiService from '../services/api';

interface SetPasswordFormData {
  password: string;
  confirmPassword: string;
}

interface LocationState {
  adminId: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

const SetPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCheck, setIsLoadingCheck] = useState(false);
  const [adminData, setAdminData] = useState<LocationState | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetPasswordFormData>();

  const password = watch('password');

  // Use useEffect to set initial state to prevent infinite re-renders
  React.useEffect(() => {
    if (!state?.adminId && !adminData) {
      setShowEmailForm(true);
    }
    setIsInitialized(true);
  }, [state?.adminId, adminData]);

  const onSubmit = async (data: SetPasswordFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const adminId = state?.adminId || adminData?.adminId;
    if (!adminId) {
      toast.error('Admin ID not found');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.setAdminPassword(adminId, data.password);
      
      if (response.success) {
        toast.success('Password set successfully! You can now login.');
        navigate('/login');
      } else {
        throw new Error(response.message || 'Failed to set password');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailCheck = async (email: string) => {
    setIsLoadingCheck(true);
    try {
      const response = await apiService.checkAdminPasswordStatus(email);
      
      if (response.success && response.data?.hasPassword === false) {
        setAdminData({
          adminId: response.data.admin.id,
          email: response.data.admin.email,
          firstName: response.data.admin.firstName,
          lastName: response.data.admin.lastName,
          role: response.data.admin.role,
        });
        setShowEmailForm(false);
        toast.success('Admin found! Please set your password.');
      } else if (response.success && response.data?.hasPassword === true) {
        toast.error('This admin already has a password set. Please login normally.');
      } else {
        toast.error('Admin not found or already has password set.');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to check admin status');
    } finally {
      setIsLoadingCheck(false);
    }
  };

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show email form if no admin data
  if (showEmailForm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 bg-primary rounded-lg flex items-center justify-center">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Set Admin Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your admin email to set or reset your password
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={(e) => {
            e.preventDefault();
            const emailInput = e.currentTarget.querySelector('input[type="email"]') as HTMLInputElement;
            if (emailInput?.value) {
              handleEmailCheck(emailInput.value);
            }
          }}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Admin Email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Enter admin email"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                className="w-full"
                loading={isLoadingCheck}
              >
                Check Admin Status
              </Button>
              
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Get admin data from state or stored data
  const currentAdmin = state || adminData;
  if (!currentAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-primary rounded-lg flex items-center justify-center">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Welcome {currentAdmin.firstName} {currentAdmin.lastName}! Please set your password to continue.
          </p>
          <p className="mt-1 text-center text-xs text-gray-500">
            {currentAdmin.email} • {currentAdmin.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === password || 'Passwords do not match',
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="submit"
              className="w-full"
              loading={isLoading}
            >
              Set Password
            </Button>
            
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetPassword;
